import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useNotify } from '@/hooks/useNotify';

import { formatCurrency } from '@/lib/utils';
import { useImpersonationGuard } from '@/hooks/useImpersonationGuard';

export interface PaymentData {
  invoiceId: string;
  paymentDate: Date;
  paymentAmount: number;
  paymentMethod: string;
  notes?: string;
}

export interface PaymentResult {
  success: boolean;
  status: 'paid' | 'partially_paid';
  overpaymentAmount?: number;
  remainingBalance?: number;
  estimateId?: string;
}

export interface WriteOffData {
  invoiceId: string;
  amount: number;
  reason: string;
}

export interface PaymentReversalData {
  paymentId: string;
  reason: string;
}

export const usePaymentRecording = (invoiceId: string) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { isReadOnly } = useImpersonationGuard();

  const recordPaymentMutation = useMutation({
    mutationFn: async (data: PaymentData): Promise<PaymentResult> => {
      if (isReadOnly) throw new Error('Cannot record payments in View Only mode');
      // Get current invoice data
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .select('total_amount, paid_amount, credit_applied, status, customer_id, created_by, estimate_id')
        .eq('id', data.invoiceId)
        .single();

      if (invoiceError) throw invoiceError;

      const totalAmount = invoice.total_amount || 0;
      const creditApplied = invoice.credit_applied || 0;
      const netAmount = totalAmount - creditApplied;
      const currentPaidAmount = invoice.paid_amount || 0;
      const remainingBalance = netAmount - currentPaidAmount;
      
      // Calculate new paid amount and overpayment relative to NET amount
      const newPaidAmount = currentPaidAmount + data.paymentAmount;
      const overpaymentAmount = newPaidAmount > netAmount ? newPaidAmount - netAmount : 0;
      
      // Determine new status based on NET amount
      let newStatus: 'paid' | 'partially_paid';
      if (newPaidAmount >= netAmount) {
        newStatus = 'paid';
      } else {
        newStatus = 'partially_paid';
      }

      // Create payment record - the database trigger handles invoice updates
      const { error: paymentError } = await supabase
        .from('invoice_payments')
        .insert({
          invoice_id: data.invoiceId,
          payment_date: data.paymentDate.toISOString(),
          payment_amount: data.paymentAmount,
          payment_method: data.paymentMethod,
          notes: data.notes,
          created_by: invoice.created_by
        });

      if (paymentError) throw paymentError;

      // Invoice status is now updated by database trigger (calculate_invoice_payment_status)
      // No manual update needed here

      return {
        success: true,
        status: newStatus,
        overpaymentAmount: overpaymentAmount > 0 ? overpaymentAmount : undefined,
        remainingBalance: newStatus === 'partially_paid' ? netAmount - newPaidAmount : 0,
        estimateId: invoice.estimate_id
      };
    },
    onSuccess: (result) => {
      if (result.status === 'paid') {
        notify.success('Payment recorded - Invoice marked as paid');
      } else {
        notify.success(`Partial payment recorded - ${formatCurrency(result.remainingBalance || 0)} remaining`);
      }
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', invoiceId] });
      if (result.estimateId) {
        queryClient.invalidateQueries({ queryKey: ['estimateInvoices', result.estimateId] });
      }
    },
    onError: (error) => {
      console.error('Error recording payment:', error);
      notify.error(error);
    }
  });

  const quickPayMutation = useMutation({
    mutationFn: async () => {
      if (isReadOnly) throw new Error('Cannot record payments in View Only mode');
      // Get current invoice data
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .select('total_amount, paid_amount, credit_applied, created_by, estimate_id')
        .eq('id', invoiceId)
        .single();

      if (invoiceError) throw invoiceError;

      const totalAmount = invoice.total_amount || 0;
      const creditApplied = invoice.credit_applied || 0;
      const netAmount = totalAmount - creditApplied;
      const currentPaidAmount = invoice.paid_amount || 0;
      const remainingBalance = netAmount - currentPaidAmount;

      if (remainingBalance <= 0) {
        throw new Error('Invoice is already fully paid');
      }

      // Create payment record for remaining NET balance - trigger handles invoice update
      const { error: paymentError } = await supabase
        .from('invoice_payments')
        .insert({
          invoice_id: invoiceId,
          payment_date: new Date().toISOString(),
          payment_amount: remainingBalance,
          payment_method: 'EFT',
          notes: 'Quick payment - marked as paid',
          created_by: invoice.created_by
        });

      if (paymentError) throw paymentError;

      // Invoice status is now updated by database trigger (calculate_invoice_payment_status)
      // No manual update needed here
      
      return { estimateId: invoice.estimate_id };
    },
    onSuccess: (result) => {
      notify.success('Invoice marked as paid');
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', invoiceId] });
      if (result?.estimateId) {
        queryClient.invalidateQueries({ queryKey: ['estimateInvoices', result.estimateId] });
      }
    },
    onError: (error) => {
      console.error('Error marking as paid:', error);
      notify.error(error);
    }
  });

  const createCreditFromOverpayment = useMutation({
    mutationFn: async ({ 
      customerId, 
      overpaymentAmount, 
      invoiceId: sourceInvoiceId 
    }: { 
      customerId: string; 
      overpaymentAmount: number; 
      invoiceId: string;
    }): Promise<{ creditNoteNumber?: string; alreadyExisted?: boolean; reactivated?: boolean }> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if credit already exists for this invoice (active OR cancelled)
      const { data: existingCredit } = await supabase
        .from('customer_credits')
        .select('id, credit_note_number, status')
        .eq('source_invoice_id', sourceInvoiceId)
        .eq('source_type', 'overpayment')
        .maybeSingle();

      if (existingCredit) {
        if (existingCredit.status === 'cancelled') {
          // Reactivate the cancelled credit with new amount
          const { error: reactivateError } = await supabase
            .from('customer_credits')
            .update({
              credit_amount: overpaymentAmount,
              remaining_amount: overpaymentAmount,
              status: 'issued',
              cancelled_at: null,
              cancelled_by: null,
              cancelled_reason: null,
              credit_note_issued_at: new Date().toISOString(),
              credit_note_issued_by: user.id,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingCredit.id);

          if (reactivateError) throw reactivateError;

          return { 
            creditNoteNumber: existingCredit.credit_note_number || 'Unknown',
            reactivated: true 
          };
        } else {
          // Credit exists and is active - return success (idempotent)
          return { 
            creditNoteNumber: existingCredit.credit_note_number || 'Unknown',
            alreadyExisted: true 
          };
        }
      }

      // Get company ID for credit note generation
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('Company not found');

      // Create the credit record - company_id and credit_note_number assigned by trigger
      const { error } = await (supabase as any)
        .from('customer_credits')
        .insert({
          customer_id: customerId,
          credit_amount: overpaymentAmount,
          remaining_amount: overpaymentAmount,
          source_type: 'overpayment',
          reason_type: 'overpayment',
          source_invoice_id: sourceInvoiceId,
          description: `Credit from overpayment on invoice`,
          created_by: user.id,
          status: 'issued',
          credit_note_issued_at: new Date().toISOString(),
          credit_note_issued_by: user.id,
        });

      if (error) throw error;

      return {};
    },
    onSuccess: (result: any) => {
      if (result.alreadyExisted) {
        notify.info(`Credit ${result.creditNoteNumber} already exists for this invoice`);
      } else if (result.reactivated) {
        notify.success(`Credit ${result.creditNoteNumber} reactivated from overpayment`);
      } else {
        notify.success(`Customer credit created from overpayment`);
      }
      queryClient.invalidateQueries({ queryKey: ['customer-credits'] });
    },
    onError: (error: any) => {
      // Handle unique constraint violation gracefully (race condition)
      if (error?.code === '23505') {
        notify.info('Credit already exists for this overpayment');
        queryClient.invalidateQueries({ queryKey: ['customer-credits'] });
        return;
      }
      console.error('Error creating credit:', error);
      notify.error(error);
    }
  });

  // Write-off mutation
  const writeOffMutation = useMutation({
    mutationFn: async (data: WriteOffData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get current invoice data
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .select('total_amount, paid_amount, credit_applied')
        .eq('id', data.invoiceId)
        .single();

      if (invoiceError) throw invoiceError;

      const netAmount = (invoice.total_amount || 0) - (invoice.credit_applied || 0);
      const remainingBalance = netAmount - (invoice.paid_amount || 0);
      if (data.amount > remainingBalance) {
        throw new Error('Write-off amount exceeds remaining balance');
      }

      // Create write-off record
      const { error: writeOffError } = await supabase
        .from('write_offs')
        .insert({
          invoice_id: data.invoiceId,
          amount: data.amount,
          reason: data.reason,
          written_off_by: user.id
        });

      if (writeOffError) throw writeOffError;

      // Update invoice - check against NET amount for status
      const newPaidAmount = (invoice.paid_amount || 0) + data.amount;
      const { error: updateError } = await supabase
        .from('invoices')
        .update({
          paid_amount: newPaidAmount,
          status: newPaidAmount >= netAmount ? 'paid' : 'partially_paid',
          paid_at: newPaidAmount >= netAmount ? new Date().toISOString() : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', data.invoiceId);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      notify.success('Balance written off successfully');
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['write-offs', invoiceId] });
    },
    onError: (error) => {
      console.error('Error writing off balance:', error);
      notify.error(error);
    }
  });

  // Payment reversal mutation
  const reversePaymentMutation = useMutation({
    mutationFn: async (data: PaymentReversalData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Mark payment as cancelled
      const { error: paymentError } = await supabase
        .from('invoice_payments')
        .update({
          is_cancelled: true,
          cancelled_at: new Date().toISOString(),
          cancelled_reason: data.reason,
          updated_at: new Date().toISOString()
        })
        .eq('id', data.paymentId);

      if (paymentError) throw paymentError;

      // Recalculate invoice totals
      const { data: activePayments, error: paymentsError } = await supabase
        .from('invoice_payments')
        .select('payment_amount')
        .eq('invoice_id', invoiceId)
        .or('is_cancelled.is.null,is_cancelled.eq.false');

      if (paymentsError) throw paymentsError;

      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .select('total_amount, customer_id, estimate_id')
        .eq('id', invoiceId)
        .single();

      if (invoiceError) throw invoiceError;

      const newPaidAmount = (activePayments || []).reduce(
        (sum, p) => sum + (p.payment_amount || 0), 0
      );

      let newStatus = 'sent';
      if (newPaidAmount > 0 && newPaidAmount < invoice.total_amount) {
        newStatus = 'partially_paid';
      } else if (newPaidAmount >= invoice.total_amount) {
        newStatus = 'paid';
      }

      const { error: updateError } = await supabase
        .from('invoices')
        .update({
          paid_amount: newPaidAmount,
          status: newStatus,
          paid_at: newStatus === 'paid' ? new Date().toISOString() : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', invoiceId);

      if (updateError) throw updateError;

      // Cancel related credits from overpayment and reset remaining amount to zero
      const { error: creditsError } = await supabase
        .from('customer_credits')
        .update({
          status: 'cancelled',
          remaining_amount: 0,
          cancelled_at: new Date().toISOString(),
          cancelled_by: user.id,
          cancelled_reason: `Payment reversed: ${data.reason}`,
          updated_at: new Date().toISOString()
        })
        .eq('source_invoice_id', invoiceId)
        .eq('source_type', 'overpayment')
        .eq('status', 'issued');

      if (creditsError) throw creditsError;
      
      return { estimateId: invoice.estimate_id };
    },
    onSuccess: (result) => {
      notify.success('Payment reversed successfully');
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['customer-credits'] });
      if (result?.estimateId) {
        queryClient.invalidateQueries({ queryKey: ['estimateInvoices', result.estimateId] });
      }
    },
    onError: (error) => {
      console.error('Error reversing payment:', error);
      notify.error(error);
    }
  });

  return {
    recordPayment: recordPaymentMutation.mutateAsync,
    isRecording: recordPaymentMutation.isPending,
    quickPay: quickPayMutation.mutateAsync,
    isQuickPaying: quickPayMutation.isPending,
    createCreditFromOverpayment: createCreditFromOverpayment.mutateAsync,
    isCreatingCredit: createCreditFromOverpayment.isPending,
    writeOff: writeOffMutation.mutateAsync,
    isWritingOff: writeOffMutation.isPending,
    reversePayment: reversePaymentMutation.mutateAsync,
    isReversingPayment: reversePaymentMutation.isPending
  };
};
